import { useState, useEffect } from 'react';

const PRIORITY_TYPES = [
    { key: 'Alert', label: 'Alert' },
    { key: 'Assist', label: 'Assist' },
    { key: 'CallCordMissing', label: 'Call Cord Missing' },
    { key: 'Fall', label: 'Fall' },
];

const AlertSettingsTab = ({
    acceptEnabled,
    finishEnabled,
    reasonEnabled,
    releaseTimeout,
    finishTimeout,
    clearTimeout: clearTimeoutProp,
    highPriorityTypes,
    onSave,
    saving,
}) => {
    const [accept, setAccept] = useState(!!acceptEnabled);
    const [finish, setFinish] = useState(!!finishEnabled);
    const [reason, setReason] = useState(!!reasonEnabled);
    const [release, setRelease] = useState(releaseTimeout || 1);
    const [finishTime, setFinishTime] = useState(finishTimeout || 5);
    const [clearTime, setClearTime] = useState(clearTimeoutProp || 4);
    const [priorities, setPriorities] = useState(highPriorityTypes || []);
    const [dirty, setDirty] = useState(false);

    // Sync from props on data reload
    useEffect(() => {
        setAccept(!!acceptEnabled);
        setFinish(!!finishEnabled);
        setReason(!!reasonEnabled);
        setRelease(releaseTimeout || 1);
        setFinishTime(finishTimeout || 5);
        setClearTime(clearTimeoutProp || 4);
        setPriorities(highPriorityTypes || []);
        setDirty(false);
    }, [acceptEnabled, finishEnabled, reasonEnabled, releaseTimeout, finishTimeout, clearTimeoutProp, highPriorityTypes]);

    // Cascading toggle logic
    const toggleAccept = () => {
        const next = !accept;
        setAccept(next);
        if (!next) {
            setFinish(false);
            setReason(false);
        }
        setDirty(true);
    };

    const toggleFinish = () => {
        const next = !finish;
        setFinish(next);
        if (!next) {
            setReason(false);
        }
        setDirty(true);
    };

    const toggleReason = () => {
        setReason(!reason);
        setDirty(true);
    };

    // Range slider handlers with auto-validation
    const handleClearChange = (val) => {
        const v = parseInt(val);
        setClearTime(v);
        // Auto-adjust: Finish must be > Clear
        if (v >= finishTime) {
            setFinishTime(v + 1);
        }
        setDirty(true);
    };

    const handleFinishChange = (val) => {
        const v = parseInt(val);
        if (v > clearTime) {
            setFinishTime(v);
            setDirty(true);
        }
    };

    const togglePriority = (type) => {
        setPriorities((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
        setDirty(true);
    };

    const handleSave = () => {
        onSave({
            AcceptButtonEnabled: accept,
            FinishButtonEnabled: finish,
            ReasonCodeEnabled: reason,
            ReleaseTimeInterval: parseInt(release),
            FinishTimeInterval: parseInt(finishTime),
            ClearTimeInterval: parseInt(clearTime),
            HighPriorityAlertTypes: priorities,
        });
    };

    // Slider position helpers
    const releasePercent = ((release - 1) / (60 - 1)) * 100;
    const finishPercent = ((finishTime - 5) / (60 - 5)) * 100;
    const clearPercent = ((clearTime - 4) / (59 - 4)) * 100;

    const toggleRows = [
        {
            key: 'accept',
            title: 'Accept Alerts',
            desc: 'Users must accept alerts being responded to.',
            value: accept,
            onChange: toggleAccept,
            disabled: false,
        },
        {
            key: 'finish',
            title: 'Finish Alerts',
            desc: 'Users must mark when alerts are complete.',
            value: finish,
            onChange: toggleFinish,
            disabled: !accept,
        },
        {
            key: 'reason',
            title: 'Record Reason',
            desc: 'Users can record a reason for the alert.',
            value: reason,
            onChange: toggleReason,
            disabled: !accept || !finish,
        },
    ];

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">Alert Interactions</h3>
                {dirty ? (
                    <div className="pp-section__actions">
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="pp-section__body">
                {/* Toggle rows with cascading */}
                {toggleRows.map((t) => (
                    <div key={t.key} className={`ss-toggle-row${t.disabled ? ' is-disabled' : ''}`}>
                        <div className="ss-toggle-row__info">
                            <h4 className="ss-toggle-row__title">{t.title}</h4>
                            <p className="ss-toggle-row__desc">{t.desc}</p>
                        </div>
                        <div className="ss-toggle-row__control">
                            <div
                                className={`ss-toggle__track${t.value ? ' is-active' : ''}`}
                                onClick={t.disabled ? undefined : t.onChange}
                                role="switch"
                                aria-checked={t.value}
                                tabIndex={t.disabled ? -1 : 0}
                                onKeyDown={(e) => {
                                    if (!t.disabled && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        t.onChange();
                                    }
                                }}
                            >
                                <div className="ss-toggle__knob" />
                            </div>
                        </div>
                    </div>
                ))}

                {/* Release Timeout slider */}
                <div className="ss-range">
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Release Timeout</h4>
                    </div>
                    <p className="ss-range__desc">
                        Time to release alert after accepted with no device clear.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${releasePercent}% + ${10 - releasePercent * 0.175}px)` }}
                        >
                            {release} Min
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={1}
                            max={60}
                            step={1}
                            value={release}
                            onChange={(e) => { setRelease(parseInt(e.target.value)); setDirty(true); }}
                        />
                    </div>
                    <div className="ss-range__labels"><span>1 Min</span><span>60 Min</span></div>
                </div>

                {/* Finish Timeout slider */}
                <div className="ss-range">
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Finish Timeout</h4>
                    </div>
                    <p className="ss-range__desc">
                        Time to auto-finish alarm if stale after accept and clear.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${finishPercent}% + ${10 - finishPercent * 0.175}px)` }}
                        >
                            {finishTime} Min
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={5}
                            max={60}
                            step={1}
                            value={finishTime}
                            onChange={(e) => handleFinishChange(e.target.value)}
                        />
                    </div>
                    <div className="ss-range__labels"><span>5 Min</span><span>60 Min</span></div>
                </div>

                {/* Clear Timeout slider */}
                <div className="ss-range">
                    <div className="ss-range__header">
                        <h4 className="ss-range__title">Clear Timeout</h4>
                    </div>
                    <p className="ss-range__desc">
                        Time to auto-clear alert after no contact from system.
                    </p>
                    <div className="ss-range__track-wrap">
                        <span
                            className="ss-range__value"
                            style={{ left: `calc(${clearPercent}% + ${10 - clearPercent * 0.175}px)` }}
                        >
                            {clearTime} Min
                        </span>
                        <input
                            className="ss-range__input"
                            type="range"
                            min={4}
                            max={59}
                            step={1}
                            value={clearTime}
                            onChange={(e) => handleClearChange(e.target.value)}
                        />
                    </div>
                    <div className="ss-range__labels"><span>4 Min</span><span>59 Min</span></div>
                </div>

                {/* High Priority Alert Types */}
                <div style={{ marginTop: 'var(--space-4)' }}>
                    <h4 className="ss-range__title">High Priority Alert Types</h4>
                    <p className="ss-range__desc" style={{ marginBottom: 'var(--space-2)' }}>
                        Alert types that behave like high priority.
                    </p>
                    <div className="ss-checkbox-row">
                        {PRIORITY_TYPES.map((pt) => (
                            <label key={pt.key} className="ss-checkbox-item">
                                <input
                                    className="ss-checkbox-item__input"
                                    type="checkbox"
                                    checked={priorities.includes(pt.key)}
                                    onChange={() => togglePriority(pt.key)}
                                />
                                <span className="ss-checkbox-item__label">{pt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlertSettingsTab;
